import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { withRouter, WithRouterProps } from 'react-router';

import { RCCheckbox } from '@reverbdotcom/cadence/components';

import FacetOption from './facet_option';
import FilterLink from './filter_link';
import { withPrefix } from '../i18n_helpers';
import { Filter, FilterOption } from './search_filters';
import ProximityModal, { POSTAL_CODE_PARAM, DISTANCE_PARAM, MILES_100 } from './proximity_modal';
import { useUser } from '../user_hooks';
import { useUpdateUrl } from './with_update_url';
import { linkParams } from './select_facet_option';

interface IExternalProps {
  option: FilterOption;
  trackInteraction: () => void;
  inSortContext?: boolean;
}

type IProps = IExternalProps & WithRouterProps;

const t = withPrefix('commons.proximityFilter');

export const PROXIMITY_PARAM = 'proximity';
export const PROXIMITY_SORT_ASCENDING = 'proximity|asc';

export function proximityParams(baseParams: ReturnType<typeof linkParams>, postalCode: string, distance: string, inSortContext = false) {
  const sortParams = inSortContext ? { sort: PROXIMITY_SORT_ASCENDING } : {};
  return {
    [POSTAL_CODE_PARAM]: postalCode,
    [DISTANCE_PARAM]: distance,
    ...sortParams,
    ...baseParams,
  };
}

export function getProximityFilterText(distance: string, postalCode: string, inSortContext: boolean) {
  if (inSortContext) {
    return postalCode ?
      I18n.t('commons.proximityFilter.sortNameWithZip', { postalCode }) :
      I18n.t('commons.proximityFilter.sortName');
  }

  return postalCode ?
    I18n.t('commons.proximityFilter.filterNameWithZip', { distance, postalCode }) :
    I18n.t('commons.proximityFilter.filterName', { distance });
}

function getProximitySubFilter(option: FilterOption): Filter {
  return option.subFilter || ({} as Filter);
}

export function ProximityFacet({
  option,
  location,
  trackInteraction,
  inSortContext = false,
  router,
}: IProps) {
  const { updateUrl } = useUpdateUrl();
  const user = useUser();
  const [modal, openModal] = React.useState(false);
  const inFilterContext = !inSortContext;

  const postalCodeFilter = getProximitySubFilter(option);
  const postalCodeOption = postalCodeFilter.options?.[0] || ({} as FilterOption);

  const distanceFilter = getProximitySubFilter(postalCodeOption);
  const distanceOptions = distanceFilter.options || [];
  const distanceOptionsValues = distanceOptions.map((o) => [o.name, o.optionValue, o.selected]);
  const distanceOption = distanceOptions.find((o) => o.selected) || ({} as FilterOption);

  const currentPostalCode = postalCodeOption.optionValue || location?.query?.postal_code || user.postalCode;
  const currentDistance = distanceOption.optionValue || location?.query?.distance || MILES_100;

  const proximityFilterText = getProximityFilterText(currentDistance, currentPostalCode, inSortContext);

  function handleClick(event) {
    if (!currentPostalCode) {
      event.preventDefault();
      openModal(true);
      return;
    }

    const newUrl = updateUrl({
      location,
      newParams: proximityParams(
        linkParams(option, true),
        currentPostalCode,
        currentDistance,
        inSortContext,
      ),
    });

    trackInteraction();

    router.push(newUrl);
  }

  const editButton = (
    <button
      type="button"
      className={classNames(
        'button-as-link',
        { 'padding-half': inFilterContext },
      )}
      onClick={(e) => {
        e.preventDefault();
        openModal(true);
      }}
    >
      <span className="text-utility-350 text-utility--semibold">
        {t('modalButton')}
      </span>
    </button>
  );

  const linkClass = classNames({
    facet__link: true,
    'facet__link--active': option.selected,
  });

  return (
    <>
      {inFilterContext && (
        <div className="d-flex gap-1 fx-align-start">
          <RCCheckbox
            id={option.optionValue}
            checked={option.selected}
            onChange={handleClick}
            label={proximityFilterText}
            paddedHover
            // when there is no postal code, leverage the tri-state checkbox's onClick to open Proximity modal
            triState={!currentPostalCode}
            onClick={handleClick}
          />
          {editButton}
        </div>
      )}
      {inSortContext && (
        <FacetOption
          paramName="proximity"
          active={option.selected}
        >
          <span className="mr-2">
            <FilterLink
              className={linkClass}
              params={proximityParams(linkParams(option, true), currentPostalCode, currentDistance, inSortContext)}
              data-checked={option.selected || null}
              rel="nofollow"
              onClick={handleClick}
              trackInteraction={trackInteraction}
            >
              {proximityFilterText}
            </FilterLink>
          </span>
          {editButton}
        </FacetOption>
      )}
      <ProximityModal
        isModalOpen={modal}
        setModal={openModal}
        propsPostalCode={currentPostalCode}
        propsDistance={currentDistance}
        propsDistanceOptions={distanceOptionsValues}
        includeRadiusDropdown={inFilterContext}
        additionalQueryParams={inSortContext ? { sort: PROXIMITY_SORT_ASCENDING } : { [PROXIMITY_PARAM]: true }}
        shouldApplyDistanceQueryParam={inFilterContext}
        shouldApplyPostalCodeQueryParam
      />
    </>
  );
}

export default withRouter<IExternalProps>(ProximityFacet);
